<template>
  <BModal
    v-model="showModal"
    title="Add License from Ticket ID"
    centered
    lazy
    no-footer
    size="lg"
    :autofocus="false"
    @shown="focusTicketId"
    @hidden="close"
    @hide="hideModal"
  >
    <div class="modal-body p-0">
      <div class="row">
        <div class="col-12">
          <div class="row">
            <div class="col-12">
              <p class="mb-0 flex-grow-1">
                If you have bought a license ticket from your Phoenix Contact
                sales subsidiary, you can import the ticket ID here.
              </p>
            </div>
          </div>
          <FormWithValidation
            :key="formKey"
            v-model="importTicketIdForm"
            col-class="col-12"
            :initial-state="initialState"
            :validation-rules-function="
              () => getImportTicketIdFormRules(importTicketIdForm)
            "
            :validation-messages="validationMessages"
            :field-labels-map="fieldLabelsMap"
            :saved-callback="importTicketId"
          >
            <template #ticketId="slotProps">
              <FormField
                id="ticketId"
                name="ticketId"
                label="Ticket ID"
                :mandatory="true"
                :v-field="slotProps.vForm.ticketId"
                :validation-messages="slotProps.validationMessages.ticketId"
                :field-errors="slotProps.fieldErrors"
                :class="slotProps.getIsInvalidClass(slotProps.vForm.ticketId)"
              >
                <InputField
                  :id="ticketIdInput"
                  v-model="importTicketIdForm.ticketId"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.ticketId)"
                  name="ticketId"
                  placeholder="(e.g. XXXX-XXXX-XXXX-XX)"
                  class=""
                />
              </FormField>
            </template>
            <template #cancel-button>
              <StoreButton @click="close">
                <span>Cancel</span>
              </StoreButton>
            </template>
          </FormWithValidation>
        </div>
      </div>
    </div>
  </BModal>
</template>

<script lang="ts" setup>
import { BModal } from "bootstrap-vue-next";
import FormWithValidation from "~/components/shared/forms/FormWithValidation.vue";
import FormField from "~/components/shared/forms/FormField.vue";
import InputField from "~/components/shared/inputfields/InputField.vue";
import { useUiStore } from "~/stores/ui/uistore";
import {
  getImportTicketIdFormRules,
  type ImportTicketIdForm,
  validationMessages,
  fieldLabelsMap,
} from "~/validations/import-ticketid";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";

const showModal = defineModel<boolean>({ default: false });
const formKey = ref(0);

function close() {
  reset();
  showModal.value = false;
}

function reset() {
  importTicketIdForm.ticketId = "";
  initialState.ticketId = "";
  formKey.value += 1; // vue rerender so that everything is reset
}

const ticketIdInput = "ticketIdInput";

// Try focusing a few times because BVN modal focus-trap can steal it back
// workaround..
async function focusTicketId() {
  await nextTick();

  const start = performance.now();
  const timeoutMs = 400; // short and sweet
  const selector = ".modal.show input#" + ticketIdInput;

  const tryFocus = () => {
    const input = document.querySelector(selector) as HTMLInputElement | null;
    if (input) {
      input.focus();
      input.select?.();
      // verify focus actually “stuck”
      if (document.activeElement === input) return;
    }
    if (performance.now() - start < timeoutMs) {
      requestAnimationFrame(tryFocus);
    }
  };

  requestAnimationFrame(tryFocus);
}

const props = defineProps({
  callbackFunction: { type: Function, required: true },
});

const initialState = reactive({
  ticketId: "",
});

const importTicketIdForm = reactive<ImportTicketIdForm>({
  ...initialState,
});

const hideModal = () => {
  useUiStore().isFormDirty = false;
};

const importTicketId = async (): Promise<void> => {
  await props.callbackFunction(importTicketIdForm);
};
</script>
<!-- scoped - does not work here because the bootstrap modal uses teleports -->
<style lang="scss"></style>
